import { useState } from 'react';
import postResolvePendingDispersion, {
  ResolveDecision,
} from '../services/postResolvePendingDispersion';

interface AlertMessage {
  description: string;
  variant: 'error' | 'success';
}

interface Status {
  loading: boolean;
  alertMessage: AlertMessage;
  success: boolean;
  error: boolean;
}

export type PendingDispersionActions = 'APPROVE' | 'REJECT';

export interface SelectedPendingDispersion {
  id: string;
  companyName: string;
  formattedAmount: string;
  action: PendingDispersionActions;
}

type MessageType = {
  [key: string]: AlertMessage;
};

const ACTION_TO_DECISION: Record<PendingDispersionActions, ResolveDecision> = {
  APPROVE: 'APPROVED',
  REJECT: 'REJECTED',
};

/**
 * Approve / reject pending dispersion creation state hook
 * @param handleRefresh Function to refresh the list
 */
const useResolvePendingDispersion = (handleRefresh: () => void) => {
  const MESSAGES: MessageType = {
    ERROR_GENERIC: {
      description: 'COMMON:ERRORS:IT_ERROR',
      variant: 'error',
    },
    SUCCESS_APPROVED: {
      description: 'PENDING_DISPERSIONS:ALERT_MESSAGES:APPROVED',
      variant: 'success',
    },
    SUCCESS_REJECTED: {
      description: 'PENDING_DISPERSIONS:ALERT_MESSAGES:REJECTED',
      variant: 'success',
    },
    ERROR_APPROVED: {
      description: 'PENDING_DISPERSIONS:ALERT_MESSAGES:APPROVED_ERROR',
      variant: 'error',
    },
    ERROR_REJECTED: {
      description: 'PENDING_DISPERSIONS:ALERT_MESSAGES:REJECTED_ERROR',
      variant: 'error',
    },
  } as const;

  const [status, setStatus] = useState<Status>({
    loading: false,
    success: false,
    error: false,
    alertMessage: MESSAGES.ERROR_GENERIC,
  });

  const [selectedDispersion, setSelectedDispersion] = useState<SelectedPendingDispersion>({
    id: '',
    companyName: '',
    formattedAmount: '',
    action: 'APPROVE',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error: boolean) => {
    setStatus((prevState) => ({ ...prevState, error }));
  };

  /**
   * Function to set the success state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success: boolean) => {
    setStatus((prevState) => ({ ...prevState, success }));
  };

  /**
   * Function to set the success/error state with message.
   * @param success - True if success.
   * @param message - The message.
   */
  const setResponseMessage = (success: boolean, message: AlertMessage) => {
    setStatus({
      loading: false,
      error: !success,
      success,
      alertMessage: message,
    });
  };

  /**
   * Resolves a pending dispersion with APPROVED or REJECTED decision
   * @async
   * @param body - body payload request
   * @param body.id - pending dispersion id
   * @param body.decision - APPROVED or REJECTED
   */
  async function resolveDispersion(body: { id: string; decision: ResolveDecision }) {
    try {
      setStatus((prevState) => ({ ...prevState, loading: true }));
      await postResolvePendingDispersion(body);
      setTimeout(() => {
        handleRefresh();
        setResponseMessage(
          true,
          body.decision === 'APPROVED' ? MESSAGES.SUCCESS_APPROVED : MESSAGES.SUCCESS_REJECTED
        );
      }, 1000);
    } catch (error) {
      setResponseMessage(
        false,
        body.decision === 'APPROVED' ? MESSAGES.ERROR_APPROVED : MESSAGES.ERROR_REJECTED
      );
    } finally {
      setIsModalOpen(false);
    }
  }

  /**
   * Handle submit
   */
  const handleSubmit = () => {
    resolveDispersion({
      id: selectedDispersion.id,
      decision: ACTION_TO_DECISION[selectedDispersion.action],
    });
  };

  /**
   * On click action buttons
   * @param dispersion Selected pending dispersion
   */
  const onActionClick = (dispersion: SelectedPendingDispersion) => {
    setIsModalOpen(true);
    setSelectedDispersion(dispersion);
  };

  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    handleSubmit,
    isModalOpen,
    setIsModalOpen,
    selectedDispersion,
    setSelectedDispersion,
    onActionClick,
  };
};

export default useResolvePendingDispersion;
